import { Box, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import React, { FC, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { Status, SupplierStatus } from '../../service/application/ApplicationModels';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import PendingIcon from '@mui/icons-material/PendingOutlined';
import {
  putDeliveryChecklist,
  selectGetDeliveryChecklistLoading,
  selectPutDeliveryChecklistLoading,
} from '../../service/application/DeliveryChecklist';
import { LoadingButton } from '@mui/lab';
import { hasAdminPermission, hasFinancePermission } from '../../helper/AppHelper';
import { postNewApplication } from '../../service/application/NewApplicationSlice';
import { putSupplierStatus } from '../../service/application/SupplierStatusSlice';
import { postFeedbackRequest } from '../../service/application/FeedbackRequestSlice';

interface Props {
  applicationId: string;
  checklist: any;
  fetchChecklist: Function;
  getData: () => void;
  status: string;
}

const Checklist: FC<Props> = ({ applicationId, checklist, fetchChecklist, getData, status }) => {
  const [activeChecklist, setActiveChecklist] = useState<string | undefined>(undefined);
  const [sendingEmail, setSendingEmail] = useState(false);

  const dispatch = useAppDispatch();

  const getLoading = useAppSelector(selectGetDeliveryChecklistLoading);
  const putLoading = useAppSelector(selectPutDeliveryChecklistLoading);

  const handleChange = async (
    checked: boolean,
    checklistId: string,
    itemId: string,
    description: string,
  ) => {
    setActiveChecklist(itemId);
    const data = {
      applicationId,
      checklistId,
      itemId,
      isComplete: checked,
    };

    const response = await dispatch(putDeliveryChecklist(data));

    if (response.type.includes('fulfilled')) {
      // Update supplier status based on checklist item
      if (description === 'Payment in progress') {
        if (checked) {
          await dispatch(
            putSupplierStatus({
              applicationId,
              supplierStatus: SupplierStatus.PaymentInProgress,
            }),
          );
        } else {
          // When unchecking "Payment in progress", set to None
          await dispatch(
            putSupplierStatus({
              applicationId,
              supplierStatus: SupplierStatus.None,
            }),
          );
        }
        getData(); // Refresh application data to update dashboard
      } else if (description === 'Payment finalised') {
        if (checked) {
          await dispatch(
            putSupplierStatus({
              applicationId,
              supplierStatus: SupplierStatus.PaymentFinalised,
            }),
          );
        } else {
          // When unchecking "Payment finalised", revert to "Payment in progress"
          await dispatch(
            putSupplierStatus({
              applicationId,
              supplierStatus: SupplierStatus.PaymentInProgress,
            }),
          );
        }
        getData(); // Refresh application data to update dashboard
      }

      fetchChecklist();
      setActiveChecklist(undefined);
    }
  };

  const handleSendFeedbackRequest = async () => {
    setSendingEmail(true);
    const response = await dispatch(postFeedbackRequest(applicationId));

    if (response.type.includes('fulfilled')) {
      // Find the "After delivery" checklist and "Feedback request sent" item
      const afterDeliveryChecklist = checklist?.checklists.find(
        (c: any) => c.title === 'After delivery',
      );
      const feedbackRequestItem = afterDeliveryChecklist?.items.find(
        (i: any) => i.description === 'Feedback request sent',
      );

      if (afterDeliveryChecklist && feedbackRequestItem) {
        // Mark the checkbox as complete
        await dispatch(
          putDeliveryChecklist({
            applicationId,
            checklistId: afterDeliveryChecklist.id,
            itemId: feedbackRequestItem.id,
            isComplete: true,
          }),
        );
      }

      fetchChecklist(); // Refresh to show updated state
    }
    setSendingEmail(false);
  };

  const handleBtn = async () => {
    const response = await dispatch(
      postNewApplication({
        applicationId,
        applicationStatus: 'Complete',
      }),
    );
    if (response.type.includes('fulfilled')) {
      getData();
    }
  };

  return (
    <Box textAlign="left" sx={{ pl: { xs: 1, sm: 2 }, pr: { xs: 1, sm: 2 } }}>
      {checklist &&
        checklist?.checklists.map((c: any) => (
          <Box
            sx={{
              mb: 2,
              bgcolor: 'white',
              width: { xs: '100%', sm: '90%' },
              maxWidth: 600,
              p: { xs: 1, sm: 2 },
              pl: { xs: 2, sm: 3 },
            }}
          >
            <Typography
              sx={{
                fontWeight: '500',
                fontFamily: 'Roboto',
                fontSize: 16,
              }}
            >
              {c.title}
              {c.isComplete ? (
                <CheckCircleIcon sx={{ fontSize: 20, ml: 1, mb: -0.5 }} color="success" />
              ) : (
                <PendingIcon sx={{ fontSize: 20, ml: 1, mb: -0.5 }} color="warning" />
              )}
            </Typography>
            {c.items.map((i: any) => (
              <Box display="flex" alignItems="center">
                <FormControlLabel
                  sx={{ alignItems: 'inline-block', position: 'relative', width: '100%' }}
                  control={
                    <Checkbox
                      checked={i.isComplete}
                      disabled={
                        (i.description === 'Payment in progress' ||
                        i.description === 'Payment finalised'
                          ? !(hasFinancePermission() || hasAdminPermission())
                          : !hasAdminPermission()) || status === Status.Complete
                      }
                      onChange={(e) => {
                        handleChange(e.target.checked, c.id, i.id, i.description);
                      }}
                    />
                  }
                  label={
                    <Box fontSize={14}>
                      {i.description}
                      {(putLoading === 'loading' || getLoading === 'loading') &&
                        activeChecklist === i.id && (
                          <CircularProgress sx={{ mb: '-2px', ml: 1.5 }} size="15px" />
                        )}
                    </Box>
                  }
                />
                {i.description === 'Feedback request sent' &&
                  !i.isComplete &&
                  hasAdminPermission() &&
                  status !== Status.Complete && (
                    <LoadingButton
                      loading={sendingEmail}
                      size="small"
                      variant="outlined"
                      onClick={handleSendFeedbackRequest}
                      sx={{ ml: 2, minWidth: 110, whiteSpace: 'nowrap' }}
                    >
                      Send Email
                    </LoadingButton>
                  )}
                <br />
              </Box>
            ))}
          </Box>
        ))}
      {checklist?.isComplete && status === Status.Approved && (
        <LoadingButton
          loading={false}
          sx={{ width: 200 }}
          variant="contained"
          onClick={() => handleBtn()}
          autoFocus
        >
          APPLICATION COMPLETE
        </LoadingButton>
      )}
    </Box>
  );
};

export default Checklist;
